// Fleet Status shared constants + formatting. Status comes from the backend
// (`getFleetStatus`), with one override: a fridge with no IP reads as NO_IP
// whatever the evaluator said, since it had no address to be evaluated at. The
// raw value rides along as `reportedStatus` and ships in the CSV.

export const FLEET_STATUS = {
  UP: 'UP',
  DOWN: 'DOWN',
  INACCESSIBLE: 'INACCESSIBLE',
  // Frontend-only: no card row, or a card whose `status` the evaluator never
  // wrote. One bucket on purpose — triage it with the Stocking column.
  NO_DATA: 'NO_DATA',
  // Frontend-only: no address to reach, so whatever the evaluator wrote
  // describes a probe with no target. Kept apart from NO_DATA because the two
  // want different work — one is a missing record, one is a missing IP.
  NO_IP: 'NO_IP',
};

// Shared by table, filters and CSV so a status can't be spelled two ways.
// Insertion order is display order.
export const STATUS_LABELS = {
  [FLEET_STATUS.UP]: 'Up',
  [FLEET_STATUS.DOWN]: 'Down',
  [FLEET_STATUS.INACCESSIBLE]: 'Inaccessible',
  [FLEET_STATUS.NO_DATA]: 'Not Monitored',
  [FLEET_STATUS.NO_IP]: 'No IP set',
};

// Peripheral row field -> chip label, shared for the same reason.
export const PERIPHERAL_LABELS = {
  hdmi: 'HDMI',
  elo: 'ELO',
  cardReader: 'Card',
  electricalLayout: 'Elec',
};

/**
 * Which peripherals are known to be down.
 * @returns {?string[]} null when we have no data — not the same as none down.
 */
export function peripheralsDown(row) {
  const known = Object.keys(PERIPHERAL_LABELS).filter(
    (field) => row[field] !== null,
  );
  if (known.length === 0) return null;
  return known.filter((field) => !row[field]).map((f) => PERIPHERAL_LABELS[f]);
}

// Install age from the roster's onlineDate. One definition of the 28-day window,
// so the badge, the filter and the CSV can't drift apart.
export const NEWLY_INSTALLED_DAYS = 28;

export const INSTALL_AGE = {
  UPCOMING: 'UPCOMING',
  NEW: 'NEW',
  ESTABLISHED: 'ESTABLISHED',
};

export const INSTALL_AGE_LABELS = {
  [INSTALL_AGE.UPCOMING]: 'Upcoming install',
  [INSTALL_AGE.NEW]: `Newly installed (${NEWLY_INSTALLED_DAYS}d)`,
  [INSTALL_AGE.ESTABLISHED]: 'Established',
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A Date as local YYYY-MM-DD — the form onlineDate itself uses. */
export function localDateString(date = new Date()) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

// Built from parts, not `new Date(str)` — that reads YYYY-MM-DD as UTC midnight
// and lands a day early for anyone west of Greenwich.
function toLocalDate(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * The date part of an onlineDate, or null if it isn't a real calendar date.
 * The round-trip catches values the regex can't: JS rolls 2026-02-29 forward to
 * March 1 rather than rejecting it, which would quietly move a fridge a day.
 * @returns {?string} YYYY-MM-DD
 */
function usableDate(value) {
  if (typeof value !== 'string') return null;
  const date = value.slice(0, 10);
  if (!DATE_ONLY.test(date)) return null;
  return localDateString(toLocalDate(date)) === date ? date : null;
}

/**
 * Which install bucket a fridge is in.
 * @param {?string} onlineDate Roster onlineDate, date-only or a full ISO string.
 * @param {string} today YYYY-MM-DD, passed in so this stays pure.
 * @returns {?string} An INSTALL_AGE value, or null when there is no usable
 *   onlineDate — unknown, which is not the same as established.
 */
export function installAge(onlineDate, today) {
  const date = usableDate(onlineDate);
  if (!date) return null;
  // Lexical compare is exact for YYYY-MM-DD and avoids timezone arithmetic.
  // `>=` per the epic: a fridge going live today still may not be stocked.
  if (date >= today) return INSTALL_AGE.UPCOMING;
  const elapsed = toLocalDate(today).getTime() - toLocalDate(date).getTime();
  // Rounded: a DST boundary inside the window shifts this by an hour.
  const days = Math.round(elapsed / 86400000);
  return days < NEWLY_INSTALLED_DAYS
    ? INSTALL_AGE.NEW
    : INSTALL_AGE.ESTABLISHED;
}

/** Inverse of `localDateString`. @returns {?Date} null when unusable. */
export function parseLocalDate(dateStr) {
  const date = usableDate(dateStr);
  return date ? toLocalDate(date) : null;
}

function shiftDays(dateStr, days) {
  const date = toLocalDate(dateStr);
  date.setDate(date.getDate() + days);
  return localDateString(date);
}

export const ONLINE_PRESETS = {
  ONLINE_NOW: 'ONLINE_NOW',
  UPCOMING: 'UPCOMING',
  RECENT: 'RECENT',
};

// Just the name. The dropdown appends the dates each one resolves to today, so
// spelling the rule out here would say it twice there and overflow the
// applied-filter chip, which has only the name to work with.
export const ONLINE_PRESET_LABELS = {
  [ONLINE_PRESETS.ONLINE_NOW]: 'Online Now',
  [ONLINE_PRESETS.UPCOMING]: 'Upcoming',
  [ONLINE_PRESETS.RECENT]: `Recent (${NEWLY_INSTALLED_DAYS}d)`,
};

/**
 * The two presets expressed as ranges, so a chip just fills the date fields —
 * one piece of state, and no rule needed for "chip and range both set".
 * Each mirrors the badge of the same name, so the filter can never surface a
 * row the badge disagrees with.
 * @param {string} today YYYY-MM-DD
 * @returns {Object} preset key -> {from, to}; '' means open-ended.
 */
export function onlineDatePresets(today) {
  return {
    // Everything already installed. Ends yesterday, since installAge calls a
    // fridge going live today Upcoming. Open-ended at the start so the oldest
    // fridges are never cut off.
    [ONLINE_PRESETS.ONLINE_NOW]: { from: '', to: shiftDays(today, -1) },
    // Open-ended: there is no last day for "not installed yet".
    [ONLINE_PRESETS.UPCOMING]: { from: today, to: '' },
    // Ends yesterday, not today — today's installs are Upcoming, not Recent.
    [ONLINE_PRESETS.RECENT]: {
      from: shiftDays(today, -(NEWLY_INSTALLED_DAYS - 1)),
      to: shiftDays(today, -1),
    },
  };
}

/**
 * A resolved preset range as dates, so the dropdown can show exactly what the
 * chosen entry means today rather than leaving "before today" to be worked out.
 * ISO, like the Online column — no locale to disagree with.
 * @param {string} from '' for open-ended
 * @param {string} to '' for open-ended
 * @returns {string} '' when both bounds are open, which is no restriction.
 */
export function describeDateBounds(from, to) {
  if (from && to) return `${from} to ${to}`;
  if (from) return `${from} onward`;
  if (to) return `on or before ${to}`;
  return '';
}

/**
 * Is onlineDate inside [from, to]? Either bound may be '' for open-ended.
 * @returns {boolean} False for an unusable onlineDate once a bound is set —
 *   unknown cannot be claimed to fall inside a range.
 */
export function matchesOnlineRange(onlineDate, from, to) {
  if (!from && !to) return true;
  const date = usableDate(onlineDate);
  if (!date) return false;
  // Lexical compare again — exact for YYYY-MM-DD, no timezone arithmetic.
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

// eMaint work orders. The payload is not self-consistent — `date_wo` arrives
// as ISO ("2026-03-13T00:00:00.000Z") from one path and as MM/DD/YY
// ("05/22/26") from another, `time_zone` is sometimes "", and `sch_date` is
// sometimes absent entirely. Everything below tolerates all of that and returns
// null rather than guessing.

const WO_ISO = /^\d{4}-\d{2}-\d{2}/;
const WO_US_SHORT = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/;

/**
 * When a work order was opened.
 * @param {?string} dateWo
 * @returns {?number} Epoch seconds, or null if the value is unusable.
 */
export function workOrderOpenedAt(dateWo) {
  if (typeof dateWo !== 'string') return null;
  const value = dateWo.trim();
  if (WO_ISO.test(value)) {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
  }
  const parts = WO_US_SHORT.exec(value);
  if (!parts) return null;
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  // eMaint's two-digit years are this century: 26 is 2026, not 1926.
  const year = 2000 + Number(parts[3]);
  const date = new Date(year, month - 1, day);
  // Rejects 13/45/26 and friends, which JS would otherwise roll forward.
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return Math.floor(date.getTime() / 1000);
}

/**
 * `brief_desc` without the location id it is usually prefixed with — the row
 * already names the location, so repeating it wastes the whole cell.
 * @param {?string} desc
 * @param {string} locationId
 * @returns {string} '' when there is nothing left to show.
 */
export function workOrderShortDesc(desc, locationId) {
  if (typeof desc !== 'string') return '';
  let text = desc.trim();
  if (locationId && text.toUpperCase().startsWith(locationId.toUpperCase())) {
    text = text.slice(locationId.length).trim();
  }
  return text;
}

// PostHeartbeat's placeholders for "no reading" — storage artifacts, not values.
const VITAL_SENTINELS = new Set(['*.*', 'Version not available']);

/** Normalize a placeholder vital to '' so callers can just use `value || '—'`. */
export function cleanVital(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return VITAL_SENTINELS.has(trimmed) ? '' : trimmed;
}

// No Slack deep-link: all three URL forms were tested and none honour a search
// query (one silently shows the viewer's PREVIOUS search). Re-test before adding.

export function formatRelativeTime(epochSeconds, nowSeconds) {
  if (!epochSeconds) return 'No data';
  const diff = Math.max(0, nowSeconds - epochSeconds);
  if (diff < 60) return 'just now';
  const minutes = Math.floor(diff / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
